'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { type AuthUser, type UserRole, type TenantMembership } from '@/lib/auth';
import { createClient } from '@/lib/supabase/client';
import type { User as SupabaseUser } from '@supabase/supabase-js';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AuthContextValue {
  user: AuthUser | null;
  supabaseUser: SupabaseUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  loginAsSuperAdmin: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  hasRole: (roles: UserRole | UserRole[]) => boolean;
  hasAcceptedTerms: boolean;
  isFirstLogin: boolean;
  acceptTerms: () => void;
  completeOnboarding: () => void;
  // Multi-tenancy
  switchTenant: (tenantId: string) => Promise<{ success: boolean; error?: string }>;
  tenantMemberships: TenantMembership[];
  isSwitchingTenant: boolean;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

const SESSION_KEY = 'therapiq_user_session';
const TERMS_KEY = 'therapiq_terms_accepted';
const ONBOARDING_KEY = 'therapiq_onboarding_complete';

// ─── Helper: map Supabase user → AuthUser ─────────────────────────────────────

function mapSupabaseUser(
  sbUser: SupabaseUser,
  profile?: Record<string, unknown> | null,
  memberships?: TenantMembership[]
): AuthUser {
  const meta = sbUser.raw_user_meta_data ?? {};
  const role = (profile?.role ?? meta.role ?? 'patient') as UserRole;
  const fullName = (profile?.full_name ?? meta.full_name ?? sbUser.email ?? '') as string;
  const tenantId = (profile?.tenant_id ?? meta.tenant_id ?? undefined) as string | undefined;
  const tenantName = (profile?.tenant_name ?? meta.tenant_name ?? undefined) as string | undefined;
  const activeTenantId = (profile?.active_tenant_id ?? tenantId) as string | undefined;
  const initials = (profile?.initials ?? meta.initials ?? fullName.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()) as string;
  const subtitle = (profile?.subtitle ?? meta.subtitle ?? '') as string;

  // Resolve active tenant name from memberships
  const activeMembership = memberships?.find((m) => m.tenantId === activeTenantId);
  const activeTenantName = activeMembership?.tenantName ?? tenantName;

  // Role in active tenant (if membership exists, use that role)
  const activeRole = activeMembership?.role ?? role;

  return {
    id: sbUser.id,
    name: fullName,
    email: sbUser.email ?? '',
    role: activeRole,
    tenantId,
    tenantName,
    activeTenantId,
    activeTenantName,
    tenantMemberships: memberships ?? [],
    initials,
    subtitle,
  };
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [supabaseUser, setSupabaseUser] = useState<SupabaseUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [tenantMemberships, setTenantMemberships] = useState<TenantMembership[]>([]);
  const [isSwitchingTenant, setIsSwitchingTenant] = useState(false);

  const supabase = createClient();

  // ─── Fetch tenant memberships ────────────────────────────────────────────────

  const fetchTenantMemberships = useCallback(async (): Promise<TenantMembership[]> => {
    try {
      const { data, error } = await supabase
        .from('user_profile_tenants')
        .select(`
          tenant_id,
          role,
          is_active,
          tenants:tenant_id (
            name,
            subdomain
          )
        `)
        .eq('is_active', true);

      if (error || !data) return [];

      return data.map((row: Record<string, unknown>) => {
        const tenant = row.tenants as Record<string, unknown> | null;
        return {
          tenantId: row.tenant_id as string,
          tenantName: (tenant?.name as string) ?? '',
          tenantSubdomain: (tenant?.subdomain as string) ?? undefined,
          role: row.role as UserRole,
          isActive: row.is_active as boolean,
        };
      });
    } catch {
      return [];
    }
  }, [supabase]);

  // ─── Fetch profile from Supabase ────────────────────────────────────────────

  const fetchAndSetUser = useCallback(async (sbUser: SupabaseUser) => {
    try {
      const [{ data: profile }, memberships] = await Promise.all([
        supabase
          .from('user_profiles')
          .select('*')
          .eq('id', sbUser.id)
          .maybeSingle(),
        fetchTenantMemberships(),
      ]);

      setTenantMemberships(memberships);
      const authUser = mapSupabaseUser(sbUser, profile as Record<string, unknown> | null, memberships);
      setUser(authUser);
      setSupabaseUser(sbUser);

      // Terms & onboarding state
      const termsKey = `${TERMS_KEY}_${sbUser.id}`;
      const onboardingKey = `${ONBOARDING_KEY}_${sbUser.id}`;
      const termsAccepted = (profile as Record<string, unknown> | null)?.terms_accepted === true
        || localStorage.getItem(termsKey) === 'true';
      const onboardingDone = (profile as Record<string, unknown> | null)?.onboarding_complete === true
        || localStorage.getItem(onboardingKey) === 'true';

      setHasAcceptedTerms(termsAccepted);
      if (authUser.role === 'admin' && termsAccepted && !onboardingDone) {
        setIsFirstLogin(true);
      } else {
        setIsFirstLogin(false);
      }
    } catch {
      const authUser = mapSupabaseUser(sbUser, null, []);
      setUser(authUser);
      setSupabaseUser(sbUser);
    }
  }, [supabase, fetchTenantMemberships]);

  // ─── Session restore on mount ────────────────────────────────────────────────

  useEffect(() => {
    let mounted = true;

    const initSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user && mounted) {
          await fetchAndSetUser(session.user);
        } else if (mounted) {
          try {
            const stored = localStorage.getItem(SESSION_KEY);
            if (stored) {
              const parsed = JSON.parse(stored) as AuthUser;
              setUser(parsed);
              const termsKey = `${TERMS_KEY}_${parsed.id}`;
              const termsAccepted = localStorage.getItem(termsKey) === 'true';
              setHasAcceptedTerms(termsAccepted);
              const onboardingKey = `${ONBOARDING_KEY}_${parsed.id}`;
              const onboardingDone = localStorage.getItem(onboardingKey) === 'true';
              if (parsed.role === 'admin' && termsAccepted && !onboardingDone) {
                setIsFirstLogin(true);
              }
            }
          } catch { /* ignore */ }
        }
      } catch { /* ignore */ } finally {
        if (mounted) setIsLoading(false);
      }
    };

    initSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;
      if (session?.user) {
        await fetchAndSetUser(session.user);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setSupabaseUser(null);
        setTenantMemberships([]);
        setHasAcceptedTerms(false);
        setIsFirstLogin(false);
      }
      setIsLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchAndSetUser, supabase.auth]);

  // ─── Login ───────────────────────────────────────────────────────────────────

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });

      if (!error && data.user) {
        await fetchAndSetUser(data.user);
        setIsLoading(false);
        return { success: true };
      }

      setIsLoading(false);
      return { success: false, error: error?.message ?? 'Invalid credentials' };
    } catch (err: unknown) {
      setIsLoading(false);
      return { success: false, error: err instanceof Error ? err.message : 'Login failed' };
    }
  }, [supabase.auth, fetchAndSetUser]);

  // ─── Super Admin Login ────────────────────────────────────────────────────────

  const loginAsSuperAdmin = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });

      if (!error && data.user) {
        const meta = data.user.raw_user_meta_data ?? {};
        // Check role in user_profiles first, then fall back to metadata
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', data.user.id)
          .maybeSingle();
        const role = (profile as Record<string, unknown> | null)?.role ?? meta.role;
        if (role !== 'super_admin') {
          await supabase.auth.signOut();
          setIsLoading(false);
          return { success: false, error: 'Invalid super admin credentials' };
        }
        await fetchAndSetUser(data.user);
        setIsLoading(false);
        return { success: true };
      }

      setIsLoading(false);
      return { success: false, error: error?.message ?? 'Invalid super admin credentials' };
    } catch (err: unknown) {
      setIsLoading(false);
      return { success: false, error: err instanceof Error ? err.message : 'Login failed' };
    }
  }, [supabase, supabase.auth, fetchAndSetUser]);

  // ─── Tenant Switcher ──────────────────────────────────────────────────────────

  const switchTenant = useCallback(async (tenantId: string) => {
    if (!supabaseUser) {
      // Demo mode: switch tenant locally
      if (user) {
        const membership = user.tenantMemberships?.find((m) => m.tenantId === tenantId);
        if (membership) {
          const updated: AuthUser = {
            ...user,
            activeTenantId: tenantId,
            activeTenantName: membership.tenantName,
            role: membership.role,
          };
          setUser(updated);
          localStorage.setItem(SESSION_KEY, JSON.stringify(updated));
          return { success: true };
        }
      }
      return { success: false, error: 'Tenant not found' };
    }

    setIsSwitchingTenant(true);
    try {
      const { data, error } = await supabase.rpc('switch_active_tenant', {
        p_tenant_id: tenantId,
      });

      if (error || !data) {
        setIsSwitchingTenant(false);
        return { success: false, error: error?.message ?? 'Failed to switch tenant' };
      }

      // Re-fetch user with updated active tenant
      await fetchAndSetUser(supabaseUser);
      setIsSwitchingTenant(false);
      return { success: true };
    } catch (err: unknown) {
      setIsSwitchingTenant(false);
      return { success: false, error: err instanceof Error ? err.message : 'Switch failed' };
    }
  }, [supabase, supabaseUser, user, fetchAndSetUser]);

  // ─── Logout ──────────────────────────────────────────────────────────────────

  const logout = useCallback(async () => {
    await supabase.auth.signOut().catch(() => {});
    setUser(null);
    setSupabaseUser(null);
    setTenantMemberships([]);
    setHasAcceptedTerms(false);
    setIsFirstLogin(false);
    localStorage.removeItem(SESSION_KEY);
  }, [supabase.auth]);

  // ─── Role Check ───────────────────────────────────────────────────────────────

  const hasRole = useCallback(
    (roles: UserRole | UserRole[]) => {
      if (!user) return false;
      const arr = Array.isArray(roles) ? roles : [roles];
      return arr.includes(user.role);
    },
    [user]
  );

  // ─── Terms & Onboarding ───────────────────────────────────────────────────────

  const acceptTerms = useCallback(async () => {
    if (!user) return;
    const termsKey = `${TERMS_KEY}_${user.id}`;
    localStorage.setItem(termsKey, 'true');
    setHasAcceptedTerms(true);

    if (supabaseUser) {
      await supabase
        .from('user_profiles')
        .update({ terms_accepted: true })
        .eq('id', supabaseUser.id)
        .then(() => {});
    }

    const onboardingKey = `${ONBOARDING_KEY}_${user.id}`;
    const onboardingDone = localStorage.getItem(onboardingKey) === 'true';
    if (user.role === 'admin' && !onboardingDone) {
      setIsFirstLogin(true);
    }
  }, [user, supabaseUser, supabase]);

  const completeOnboarding = useCallback(async () => {
    if (!user) return;
    const onboardingKey = `${ONBOARDING_KEY}_${user.id}`;
    localStorage.setItem(onboardingKey, 'true');
    setIsFirstLogin(false);

    if (supabaseUser) {
      await supabase
        .from('user_profiles')
        .update({ onboarding_complete: true })
        .eq('id', supabaseUser.id)
        .then(() => {});
    }
  }, [user, supabaseUser, supabase]);

  return (
    <AuthContext.Provider value={{
      user,
      supabaseUser,
      isLoading,
      login,
      loginAsSuperAdmin,
      logout,
      hasRole,
      hasAcceptedTerms,
      isFirstLogin,
      acceptTerms,
      completeOnboarding,
      switchTenant,
      tenantMemberships,
      isSwitchingTenant,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
